// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import ProgressIndicator, { ProgressStep } from "@mcmc/App/Components/ProgressIndicator";
import setNextCheckoutStep from "@mcmc/App/Handlers/Pages/CheckoutShipping/SetNextCheckoutStep";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { progressBarStyles } from "@mcmc/App/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndPayProgressBar";
import { CheckoutShippingPageContext } from "@mcmc/Overrides/Pages/CheckoutShippingPage";
import {
    CheckoutStep,
    CheckoutStepKey,
    getCheckoutSteps,
} from "@mcmc/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmit.helper";
import keyBy from "lodash/keyBy";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: McMcApplicationState) => {
    return {
        fulfillmentMethod: state.context.session.fulfillmentMethod,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        cartId: state.pages.checkoutShipping.cartId,
        isSubmitForApprovalOrder: canSubmitForApprovalOrder(
            state.pages.checkoutShipping.cartId
                ? getCartState(state, state.pages.checkoutShipping.cartId).value
                : getCurrentCartState(state).value,
        ),
    };
};

const mapDispatchToProps = {
    setNextCheckoutStep,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const styles = progressBarStyles;

const CheckoutReviewAndPayProgressBar: FC<Props> = ({
    history,
    cartPageUrl,
    cartId,
    fulfillmentMethod,
    setNextCheckoutStep,
    isSubmitForApprovalOrder,
}) => {
    const [steps, setSteps] = useState<CheckoutStep[]>([]);
    useEffect(() => {
        if (fulfillmentMethod) {
            const steps = getCheckoutSteps(fulfillmentMethod);
            const stepsMap = keyBy(steps, "key");
            const paymentStep = stepsMap[CheckoutStepKey.payment];
            if (paymentStep) {
                paymentStep.isDisabled = isSubmitForApprovalOrder;
            }
            setSteps([...steps]);
        }
    }, [fulfillmentMethod, isSubmitForApprovalOrder]);

    const currentStep = steps[1];

    const moveToReviewAndSubmitPage = (stepHash: CheckoutStepKey) => {
        setNextCheckoutStep({ stepHash });
    };

    const moveToCartPage = () => {
        history.push(cartPageUrl || "");
    };

    const onClickHandler = (destStep: CheckoutStep, event: React.MouseEvent) => {
        switch (destStep.key) {
            case CheckoutStepKey.cart:
                event.stopPropagation();
                event.preventDefault();
                moveToCartPage();
                break;
            case CheckoutStepKey.addresses:
                event.stopPropagation();
                event.preventDefault();
                break;
            default:
                moveToReviewAndSubmitPage(destStep.key);
        }
    };

    return (
        <>
            <ProgressIndicator>
                {steps.map(step => (
                    <ProgressStep
                        key={step.key}
                        index={step.index}
                        label={step.title}
                        current={currentStep.index}
                        icon={step.icon}
                        onClick={step.isDisabled ? undefined : event => onClickHandler(step, event)}
                        disabled={step.isDisabled}
                        extendedStyles={styles.progressStep}
                        buttonType={step.index > 1 ? "submit" : "button"}
                    />
                ))}
            </ProgressIndicator>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndPayProgressBar)),

    definition: {
        group: "Checkout - Shipping",
        allowedContexts: [CheckoutShippingPageContext],
        displayName: "Checkout Progress Bar",
    },
};

export default widgetModule;
